import { atom, selector } from 'recoil'
import { DEFAULT_LRU_SIZE, keyFactory, uncachedSelector } from 'src/utils/recoil'
import { customFootfallClient } from 'src/clients'
import {
  CATCHMENT_AREA_ORIGIN_HOME_H3,
  CatchmentAreaOriginHomeH3,
  CustomFootfall,
  CustomFootfallRequest,
  CustomTradeAreaResponse,
  FootfallSectionDetails,
} from 'src/common-services'
import { CustomFootFallDateRange } from 'src/atoms/custom-polygon'

import { LAST_QUARTER } from 'src/atoms/custom-polygon/hourly-foot-traffic'
import * as customPolygonAtoms from 'src/atoms/custom-polygon'

const key = keyFactory('custom-tta')

export const dateRange = atom<CustomFootFallDateRange>({
  key: key('date-range'),
  default: LAST_QUARTER,
})

export const loading = atom<boolean>({
  key: key('loading'),
  default: false,
})

export const error = atom<string | undefined>({
  key: key('error'),
  default: undefined,
})

export const request = selector<CustomFootfallRequest | undefined>({
  key: key('request'),
  get: ({ get }) => {
    const polygonId = get(customPolygonAtoms.polygonId)
    const polygonName = get(customPolygonAtoms.polygonName)

    const multipolygon = get(customPolygonAtoms.multipolygon)
    const { start, end } = get(dateRange)

    if (!polygonId || !polygonName || !multipolygon) {
      return undefined
    }

    const request: CustomFootfallRequest = {
      id: polygonId,
      name: polygonName,
      polygon: multipolygon,
      dataset: CATCHMENT_AREA_ORIGIN_HOME_H3,
      startDate: start,
      endDate: end,
    }
    return request
  },
})

export const response = atom<CustomFootfall<CatchmentAreaOriginHomeH3> | undefined>({
  key: key('response'),
  default: undefined,
})

export const reportId = selector<string | undefined>({
  key: key('report-id'),
  get: ({ get }) => {
    const resp = get(response)
    return resp?.reportId
  },
})

export const customTradeArea = selector<CustomTradeAreaResponse | undefined>({
  key: key('current-custom-footfall-map'),
  get: async ({ get }) => {
    const resp = get(response)

    if (!resp || !resp.data) {
      return undefined
    }

    return await customFootfallClient.getCustomTradeAreaByPlaceId(resp.id)
  },
})

export const customTradeAreaMap = uncachedSelector<FootfallSectionDetails[][]>({
  key: key('custom-trade-area-map'),
  get: ({ get }) => {
    const tradeArea = get(customTradeArea)
    if (!tradeArea || !tradeArea.tradeAreaMap) {
      return []
    }
    return [tradeArea.tradeAreaMap]
  },
  lru: DEFAULT_LRU_SIZE,
})

export const customTradeAreaMapOverview = uncachedSelector<FootfallSectionDetails[][]>({
  key: key('custom-trade-area-map-overview'),
  get: ({ get }) => {
    const tradeArea = get(customTradeArea)
    if (!tradeArea || !tradeArea.tradeAreaMapOverview) {
      return []
    }
    return [tradeArea.tradeAreaMapOverview]
  },
  lru: DEFAULT_LRU_SIZE,
})

export const customTradeAreaDetails = uncachedSelector<CustomTradeAreaResponse['tradeAreaDetails'] | undefined>({
  key: key('custom-trade-area-details'),
  get: ({ get }) => {
    const tradeArea = get(customTradeArea)
    if (!tradeArea || !tradeArea.tradeAreaDetails) {
      return undefined
    }
    return tradeArea.tradeAreaDetails
  },
  lru: DEFAULT_LRU_SIZE,
})

export const radiusRings = atom<number[] | undefined>({
  key: key('radius-rings'),
  default: undefined,
})
